// EligibilityRequirements.js - New Zealand
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Skilled Employment',
      description: 'Job or job offer in skilled occupation (ANZSCO skill levels 1-3). Must be genuine full-time position paying at least median wage (NZD $31.61/hour). Employer verification required.',
      applicable: 'SMC, Work to Residence'
    },
    {
      icon: Languages,
      title: 'English Proficiency',
      description: 'IELTS 6.5 overall (no band less than 6.0) or equivalent. Alternative tests: TOEFL iBT 79, PTE Academic 58, OET Grade B. Test results valid for 2 years.',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Qualifications',
      description: 'Recognized qualification relevant to occupation. Bachelor = 40 points, Master = 50 points, PhD = 70 points. NZ qualifications receive bonus points.',
      applicable: 'SMC'
    },
    {
      icon: CheckCircle2,
      title: 'Points Score',
      description: 'Minimum 160 points to submit EOI. Competitive selection at 180+ points. Points for age, qualifications, work experience, job offer, and NZ experience.',
      applicable: 'SMC'
    },
    {
      icon: DollarSign,
      title: 'Age Requirement',
      description: 'Age 20-39 receives maximum 30 points. Age 40-44 = 20 points, 45-49 = 10 points, 50-55 = 5 points. Must be under 56 at time of application.',
      applicable: 'SMC'
    },
    {
      icon: FileText,
      title: 'Health & Character',
      description: 'Medical examination required. Police certificates from all countries lived in 12+ months. Good health and character essential. No serious criminal convictions.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            General Eligibility Requirements
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            While specific requirements vary by program, here are the common criteria for New Zealand permanent residency applications.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg border border-gray-200 hover:border-[#2542D4] transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#202356]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#202356] mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-[#2542D4] font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Skilled Migrant Category Points
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            Minimum 160 points to submit EOI. Competitive selection typically at 180+ points.
          </p>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Age (20-39)</p>
              <p className="text-white/80 font-light">30 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">PhD</p>
              <p className="text-white/80 font-light">70 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Skilled Job in NZ</p>
              <p className="text-white/80 font-light">50-60 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Partner Points</p>
              <p className="text-white/80 font-light">Up to 20 points</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> Points requirements and selection criteria change based on immigration targets. We assess your profile against current requirements and recommend the best pathway for your situation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
